import styled from "@emotion/styled";
import { colors } from "@utils/theme";
import { Form } from "formik";

export const Container = styled.div`
  margin: 0 auto;
  background: ${colors.gray50};
  min-height: 100vh;
`;

export const FormContainer = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Section = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${colors.inputBorder};
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

export const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
`;

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.gray800};
  margin: 0;
`;

export const SectionDescription = styled.p`
  font-size: 13px;
  color: ${colors.gray500};
  margin: 2px 0 0 0;
`;

export const InputGrid = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

export const Label = styled.label<{ required?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.gray700};
  display: flex;
  align-items: center;
  gap: 4px;

  ${(props) =>
    props.required &&
    `
    &::after {
      content: "*";
      color: ${colors.error};
    }
  `}
`;

export const Input = styled.input<any>`
  padding: 10px 12px;
  border: 1px solid
    ${(props) => (props.hasError ? colors.error : colors.inputBorder)};
  border-radius: 6px;
  font-size: 13px;
  background: ${colors.white};
  transition: all 0.2s ease;
  width: 95%;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.hasError ? colors.error : colors.primary};
    box-shadow: 0 0 0 2px ${colors.primaryLight};
  }

  &::placeholder {
    color: ${colors.gray400};
  }
`;

export const ErrorMessage = styled.div<any>`
  color: ${colors.error};
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Vacation Mode Components
export const VacationMode = styled.div`
  background: ${colors.gray50};
  border-radius: 8px;
  padding: 16px;
`;

export const VacationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

export const VacationLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const VacationIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
`;

export const VacationTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.gray800};
  margin: 0;
`;

export const VacationDescription = styled.p`
  font-size: 13px;
  color: ${colors.gray600};
  margin: 0;
  line-height: 1.4;
`;

export const Switch = styled.label<{ checked: boolean }>`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;

  input {
    opacity: 1;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${(props) =>
      props.checked ? colors.primary : colors.primaryLight};
    transition: 0.4s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.4s;
      border-radius: 50%;
      transform: ${(props) =>
        props.checked ? "translateX(20px)" : "translateX(0)"};
    }
  }
`;

export const DateInputWrapper = styled.div`
  width: 100%;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }

  .react-datepicker__input-container input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${colors.inputBorder};
    border-radius: 6px;
    font-size: 13px;
    background: ${colors.white};
    transition: all 0.2s ease;
    color: ${colors.gray800};

    &:focus {
      outline: none;
      border-color: ${colors.primary};
      box-shadow: 0 0 0 2px ${colors.primaryLight};
    }
  }

  .react-datepicker {
    border: 1px solid ${colors.inputBorder};
    border-radius: 12px;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    font-family: inherit;
    border: none;
  }

  .react-datepicker__header {
    background-color: ${colors.gray50};
    border-bottom: 1px solid ${colors.inputBorder};
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    padding-top: 10px;
  }

  .react-datepicker__current-month {
    color: ${colors.gray800};
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 8px;
  }

  .react-datepicker__day-name {
    color: ${colors.gray500};
    width: 32px;
    font-size: 12px;
    margin: 4px;
  }

  .react-datepicker__day {
    width: 32px;
    line-height: 32px;
    margin: 4px;
    border-radius: 6px;
    color: ${colors.gray700};
    font-size: 13px;

    &:hover {
      background-color: ${colors.primaryLight};
      color: ${colors.primaryDark};
    }

    &--selected,
    &--keyboard-selected {
      background-color: ${colors.primary} !important;
      color: ${colors.white} !important;
      box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
    }

    &--today {
      font-weight: bold;
      color: ${colors.primary};
    }

    &--outside-month {
      color: ${colors.gray300};
    }
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker__navigation {
    top: 10px;
  }
`;

export const DeliveryTimeWrapper = styled.div`
  width: 100%;

  /* Target the StyleFormWrapper inside DeliveryTime (it's a div) */
  & > div {
    gap: 0;
  }

  /* Target the internal Wrapper (main input box) */
  /* We use generic selectors because we can't import the scoped component here easily */
  & > div > div {
    background-color: ${colors.white} !important;
    border: 1px solid ${colors.inputBorder} !important;
    border-radius: 6px !important;
    padding: 4px 12px !important; /* Adjusted to approximate height */
    min-height: 27px; /* Matches standard input height approx */
    box-shadow: none !important;

    &:focus-within {
      border-color: ${colors.primary} !important;
      box-shadow: 0 0 0 2px ${colors.primaryLight} !important;
    }
  }

  /* Adjust inputs inside */
  input {
    font-size: 13px !important;
    color: ${colors.gray800} !important;
    padding: 4px !important;

    &::placeholder {
      color: ${colors.gray400} !important;
    }
  }

  /* Adjust select inside */
  select {
    font-size: 13px !important;
    color: ${colors.gray800} !important;
    font-weight: 500 !important;
  }

  /* Separator */
  span {
    color: ${colors.gray400} !important;
  }
`;

export const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${colors.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;
  align-self: flex-start;

  &:hover {
    background: ${colors.primaryDark};
  }

  &:disabled {
    background: ${colors.gray400};
    cursor: not-allowed;
  }
`;
